import { Redirect } from "@reach/router"
import { Reload } from "@shared/platform-api/requests"
import { FullPageLoader } from "@shared/primitive-components"
import { FindFleetInput, Fleet } from "@shared/types"
import { PropsWithChildren, createContext, useContext, useMemo } from "react"
import { FleetStats, FleetStatsInput, useFleetStats, WithVrns } from "../api/fleet"
import { Policy, UseFindPolicyInput, useFindPolicy, PrevPolicyDetails } from "../api/policy"
import { FromApi, useFleet } from "./hooks"
import { useAuth } from "./AuthProvider"

type ProtectedPolicyAndFleetProviderProps = {
  policy: Policy
  prevPolicies: PrevPolicyDetails[] | null
  reloadPolicy: Reload<UseFindPolicyInput>
}

export type PolicyAndFleetContextType = ProtectedPolicyAndFleetProviderProps & {
  connectivity: FromApi<FleetStats, FleetStatsInput>
  fleet: FromApi<Fleet, WithVrns<FindFleetInput>>
}

export const PolicyAndFleetContext = createContext<PolicyAndFleetContextType | undefined>(undefined)

const ProtectedPolicyAndFleetProvider = ({
  policy,
  prevPolicies,
  reloadPolicy,
  children,
}: PropsWithChildren<ProtectedPolicyAndFleetProviderProps>) => {
  const connectivity = useFleetStats({ id: policy.id })
  const fleet = useFleet({
    modern: true,
    id: policy.id,
    limit: 200,
    include: `make,model,abiGroup,trim,type,fuelType`,
    connectedVrns: connectivity.data?.connectedVrns ?? [],
  })

  const value = useMemo<PolicyAndFleetContextType>(
    () => ({
      connectivity,
      fleet,
      policy,
      prevPolicies,
      reloadPolicy,
    }),
    [connectivity, fleet, policy, prevPolicies, reloadPolicy]
  )

  if (!connectivity.data || connectivity.loading) return <FullPageLoader />

  return <PolicyAndFleetContext.Provider value={value}>{children}</PolicyAndFleetContext.Provider>
}

type Props = {
  policyId?: string
  ownerId?: string
}

export const PolicyAndFleetProvider = ({ policyId, ownerId, children }: PropsWithChildren<Props>) => {
  const { getAuthType } = useAuth()
  const authType = getAuthType()
  const { apiErrored, data, loading, reload } = useFindPolicy({ policyId, userId: ownerId, authType })

  if (apiErrored || data === null) {
    return <Redirect to="unauthorized" noThrow />
  }

  if (!data || loading) return <FullPageLoader />
  const { policy, prevPolicies } = data

  return (
    <ProtectedPolicyAndFleetProvider policy={policy} prevPolicies={prevPolicies} reloadPolicy={reload}>
      {children}
    </ProtectedPolicyAndFleetProvider>
  )
}

PolicyAndFleetProvider.defaultProps = {
  policyId: undefined,
  ownerId: undefined,
}

export const usePolicyAndFleet = () => {
  const context = useContext(PolicyAndFleetContext)
  if (context === undefined) {
    throw new Error(`usePolicyAndFleet must be used inside PolicyAndFleetProvider.`)
  }

  return context
}
